import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import * as DailyCheckinActions from './daily-checkin.actions';
import { ToastActions } from '../../../toast/toast.actions';
import { Store } from '@ngrx/store';
import { selectDailyCheckinLastQuery, selectDailyCheckinSelectedId } from './daily-checkin.selectors';
import { DailyCheckinService } from '../../../../core/services/gse/daily-checkin.service';

const normalizeErrorMessage = (error: unknown, fallback: string): string => {
  if (!error || typeof error !== 'object') {
    return fallback;
  }

  const err = error as { error?: { message?: unknown }; message?: unknown };
  const nestedMessage = typeof err.error?.message === 'string' ? err.error.message : null;
  if (nestedMessage) {
    return nestedMessage;
  }

  const directMessage = typeof err.message === 'string' ? err.message : null;
  return directMessage || fallback;
};

@Injectable()
export class DailyCheckinEffects {
  private actions$ = inject(Actions);
  private dailyCheckinService = inject(DailyCheckinService);
  private store = inject(Store);

  loadDailyCheckins$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DailyCheckinActions.loadDailyCheckins),
      switchMap(({ query }) =>
        this.dailyCheckinService.getDailyCheckins(query).pipe(
          map((result) => DailyCheckinActions.loadDailyCheckinsSuccess({ result })),
          catchError((error) =>
            of(
              DailyCheckinActions.loadDailyCheckinsFailure({
                error: normalizeErrorMessage(error, 'Failed to load daily check-ins'),
              })
            )
          )
        )
      )
    )
  );

  selectItemLoadSchedule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DailyCheckinActions.selectDailyCheckinItem),
      map(({ id }) => id),
      filter((id): id is number => typeof id === 'number' && id > 0),
      map((id) => DailyCheckinActions.loadMaintenanceSchedule({ id }))
    )
  );

  loadMaintenanceSchedule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DailyCheckinActions.loadMaintenanceSchedule),
      switchMap(({ id }) =>
        this.dailyCheckinService.getMaintenanceScheduleById(id).pipe(
          map((schedule) => DailyCheckinActions.loadMaintenanceScheduleSuccess({ schedule: schedule ?? null })),
          catchError((error) =>
            of(
              DailyCheckinActions.loadMaintenanceScheduleFailure({
                error: normalizeErrorMessage(error, 'Failed to load schedule details'),
              })
            )
          )
        )
      )
    )
  );

  createMaintenanceScheduleJobCard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DailyCheckinActions.createMaintenanceScheduleJobCard),
      mergeMap(({ payload }) =>
        this.dailyCheckinService.createMaintenanceScheduleJobCard(payload).pipe(
          mergeMap(() =>
            of(
              DailyCheckinActions.createMaintenanceScheduleJobCardSuccess(),
              ToastActions.showSuccessToast({
                summary: 'Success',
                detail: 'Job card created successfully',
              })
            )
          ),
          catchError((error) =>
            of(
              DailyCheckinActions.createMaintenanceScheduleJobCardFailure({
                error: normalizeErrorMessage(error, 'Failed to create job card'),
              }),
              ToastActions.showErrorToast({
                summary: 'Error',
                detail: normalizeErrorMessage(error, 'Failed to create job card'),
              })
            )
          )
        )
      )
    )
  );

  updateMaintenanceScheduleDailyCheckin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DailyCheckinActions.updateMaintenanceScheduleDailyCheckin),
      mergeMap(({ id, body }) =>
        this.dailyCheckinService.updateMaintenanceScheduleDailyCheckin(id, body).pipe(
          mergeMap(() =>
            of(
              DailyCheckinActions.updateMaintenanceScheduleDailyCheckinSuccess(),
              ToastActions.showSuccessToast({
                summary: 'Success',
                detail: 'Daily checkin updated successfully',
              })
            )
          ),
          catchError((error) =>
            of(
              DailyCheckinActions.updateMaintenanceScheduleDailyCheckinFailure({
                error: normalizeErrorMessage(error, 'Failed to update daily checkin'),
              }),
              ToastActions.showErrorToast({
                summary: 'Error',
                detail: normalizeErrorMessage(error, 'Failed to update daily checkin'),
              })
            )
          )
        )
      )
    )
  );

  reloadAfterSubmit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        DailyCheckinActions.createMaintenanceScheduleJobCardSuccess,
        DailyCheckinActions.updateMaintenanceScheduleDailyCheckinSuccess
      ),
      withLatestFrom(this.store.select(selectDailyCheckinLastQuery)),
      map(([, lastQuery]) =>
        DailyCheckinActions.loadDailyCheckins({
          query: lastQuery ?? {
            page: 0,
            size: 10,
            sort: 'createdAt,desc',
          },
          silent: true,
        })
      )
    )
  );

  reloadScheduleAfterUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DailyCheckinActions.updateMaintenanceScheduleDailyCheckinSuccess),
      withLatestFrom(this.store.select(selectDailyCheckinSelectedId)),
      filter(([, id]) => id != null),
      map(([, id]) => DailyCheckinActions.loadMaintenanceSchedule({ id: id! }))
    )
  );
}
